<template>
  <main-table-layout
      sub-menu-selector="adminpanel"
      tab-selector="licenseoverview"
      type="license"
  >
    <div slot="mainContent">
      <popup
          v-if="showDeletePrompt"
          @close="showDeletePrompt = false"
      >
        <delete-prompt
            slot="popupMainContent"
            @confirm="deleteLicense"
            @cancel="showDeletePrompt = false"
        />
      </popup>

      <div class="license-overview">
        <div class="license-overview-header">
          <h2>{{ $t('Licenses') }}</h2>

          <organization-switcher
              class="mt-2 mb-2"
              :organizations="$store.getters.getOrganizations"
              :show-teams="false"
              @setOrganization="setOrganization"
          />

          <div
              v-if="organizationId"
              class="license-overview-header__actions"
          >
            <div
                class="settings-button d-inline-block"
                @click="addNew"
            >
              {{ $t('add new') }}
            </div>

            <div
                class="settings-button d-inline-block ml-1"
                @click="addNewBulk"
            >
              {{ $t('create multiple') }}
            </div>

            <div
                v-if="selectedLicenses.length >= 2"
                class="settings-button settings-button--highlight d-inline-block"
                @click="openMultiEdit"
            >
              {{ $t('edit selected ({n})', { n: selectedLicenses.length }) }}
            </div>
          </div>
        </div>


        <div
            class="settings-button d-inline-block mt-2"
            @click="loadAllLicenses"
        >
          load all licenses
        </div>

        <!-- Single create/edit form -->
        <license-form
            v-if="editing && !bulkMode && !multiEditMode"
            :license="editing"
            :users="users"
            @submit="handleSubmit"
            @abort="editing = null"
        />

        <!-- Bulk creation form -->
        <license-bulk-form
            v-if="bulkMode"
            :users="users"
            @submit="handleBulkSubmit"
            @abort="bulkMode = false"
        />

        <!-- Multi-edit form -->
        <license-multi-edit-form
            v-if="multiEditMode"
            :licenses="selectedLicenses"
            :users="users"
            @submit="handleMultiEditSubmit"
            @abort="multiEditMode = false"
        />

        <license-table
            ref="licenseTable"
            :licenses="licenses"
            :users="users"
            @edit="editLicense"
            @delete="confirmDelete"
            @selection-change="onSelectionChange"
        />
      </div>
    </div>
  </main-table-layout>
</template>

<script>
import MainTableLayout from '@/layouts/FullWidthSidebarLeft.vue';
import OrganizationSwitcher from '@/components/organizations/OrganizationSwitcher.vue';
import LicenseForm from '@/components/licenses/LicenseForm.vue';
import LicenseBulkForm from '@/components/licenses/LicenseBulkForm.vue';
import LicenseMultiEditForm from '@/components/licenses/LicenseMultiEditForm.vue';
import LicenseTable from '@/components/licenses/LicenseTable.vue';
import DeletePrompt from '@/components/forms/DeletePrompt.vue';
import Popup from '@/components/Popup.vue';

export default {
  name: 'LicenseOverview',

  components: {
    Popup,
    OrganizationSwitcher,
    MainTableLayout,
    LicenseForm,
    LicenseBulkForm,
    LicenseMultiEditForm,
    LicenseTable,
    DeletePrompt
  },

  data() {
    return {
      licenses: [],
      organizationId: '',
      editing: null,
      bulkMode: false,
      multiEditMode: false,
      selectedIds: [],
      showDeletePrompt: false,
      toBeDeleted: null,
      users: []
    };
  },

  computed: {
    selectedLicenses() {
      return this.licenses.filter(l => this.selectedIds.includes(l.id));
    }
  },

  watch: {
    organizationId() {
      this.loadUsers();
    }
  },

  methods: {
    loadUsers() {
      if (!this.organizationId) {
        this.users = [];
        return;
      }

      this.$store.dispatch('clientLoadUsers', {
        filter: `homeOrganizationId eq ${this.organizationId}`
      }).then(users => {
        this.users = users;
      });
    },

    addNew() {
      this.bulkMode = false;
      this.multiEditMode = false;
      this.editing = {
        id: null,
        name: '',
        startDate: '',
        expirationDate: '',
        type: ''
      };
    },

    addNewBulk() {
      this.editing = null;
      this.multiEditMode = false;
      this.bulkMode = true;
    },

    openMultiEdit() {
      this.editing = null;
      this.bulkMode = false;
      this.multiEditMode = true;
    },

    editLicense(license) {
      this.bulkMode = false;
      this.multiEditMode = false;
      this.editing = { ...license };
    },

    onSelectionChange(ids) {
      this.selectedIds = ids;
      // Close multi-edit if selection is cleared
      if (ids.length < 2 && this.multiEditMode) {
        this.multiEditMode = false;
      }
    },

    confirmDelete(id) {
      this.toBeDeleted = id;
      this.showDeletePrompt = true;
    },

    deleteLicense() {
      this.$store.dispatch('clientDeleteLicense', [this.toBeDeleted])
          .then(() => {
            this.toBeDeleted = null;
            this.showDeletePrompt = false;
            this.loadLicenses();
          });
    },

    setOrganization(id) {
      this.organizationId = id;
      this.loadLicenses();
    },

    loadAllLicenses() {
      this.organizationId = null;
      this.loadLicenses();
    },

    loadLicenses() {
      const payload = {};

      if (this.userId) {
        payload.filter = `userId eq ${this.userId}`;
      }

      if (this.organizationId) {
        payload.filter = `organizationId eq ${this.organizationId}`;
      }

      this.$store.dispatch('clientLoadLicenses', payload)
          .then(licenses => {
            this.licenses = licenses;
            this.editing = null;
            this.bulkMode = false;
            this.multiEditMode = false;
            this.selectedIds = [];
          });
    },

    updateLicense(license) {
      return this.$store.dispatch('clientUpdateLicense', license)
          .then(() => {
            this.loadLicenses();
          });
    },

    createLicense(license) {
      const newLicense = {
        ...license,
        organizationId: this.organizationId
      };

      return this.$store.dispatch('createLicense', newLicense)
          .then(() => {
            this.loadLicenses();
          });
    },

    async handleSubmit(form) {
      if (form.id) {
        await this.updateLicense(form);
        return;
      }

      await this.createLicense(form);
    },

    async handleBulkSubmit(licenseArray) {
      const payload = licenseArray.map(license => ({
        ...license,
        organizationId: this.organizationId
      }));

      await this.$store.dispatch('clientBulkCreateLicenses', payload);
      this.loadLicenses();
    },

    async handleMultiEditSubmit(updatedLicenses) {
      await Promise.all(
          updatedLicenses.map(license =>
              this.$store.dispatch('clientUpdateLicense', license)
          )
      );
      this.loadLicenses();
    }
  }
};
</script>